import {Anchor, Group, Popover, Stack, Text, Title} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import type {FC} from 'react';
import {InfoIcon} from 'src/design/atoms/icons/CommonIcons';

/**
 * Inline anchor link that opens an info `Popover` on click, showing a title
 * and description (and optional secondary text) in the dropdown.
 * @registry Inline info link that opens a popover with title/description.
 *
 * Ported from `library-m/feedback/PermissionsNotice`. DEVIATION: stripped
 * the permission-specific defaults — `label` and `title` are now required
 * props (no defaults), and the `resolution` prop was renamed to
 * `secondaryText` to be domain-neutral.
 */
export interface NoticePopoverProps {
  /** Link label shown inline. */
  label: string;
  /** Title shown inside the popover. */
  title: string;
  /** Main explanation shown in the popover body. */
  description: string;
  /** Optional secondary line shown below the description. */
  secondaryText?: string;
}

export const NoticePopover: FC<NoticePopoverProps> = ({
  label,
  title,
  description,
  secondaryText,
}) => {
  const [opened, {toggle, close}] = useDisclosure(false);

  return (
    <Popover
      opened={opened}
      onClose={close}
      position="bottom-start"
      withArrow
      width={320}>
      <Popover.Target>
        <Anchor
          component="button"
          size="xs"
          c="dimmed"
          onClick={toggle}
          w="fit-content">
          <Group gap={3}>
            <InfoIcon /> {label}
          </Group>
        </Anchor>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Title order={6}>{title}</Title>
          <Text size="sm">{description}</Text>
          {secondaryText && <Text size="sm">{secondaryText}</Text>}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
